import type { AuthError } from '../../sessionClient';

/**
 * Flattens a register failure into the user-facing lines the form should
 * render: the top-level server message plus each field error, Rails
 * full-message style ("Email has already been taken"). The `base` bucket
 * carries whole-form messages that need no field prefix.
 *
 * Reading `message` alone is not enough: `sessionClient.register` only fills it
 * from a top-level `error` key, and Devise's `respond_with resource` failure
 * body has none — every validation message arrives under `errors.<field>`. A
 * consumer that renders only `message` shows nothing the server said.
 *
 * Returns `[]` when the error carries no words of its own, so the caller
 * chooses the generic copy. A `server-error` counts as carrying none however
 * much prose it has: a 5xx body's `error` is a status phrase ("Internal Server
 * Error"), which as an alert beside a form reads as an instruction the user
 * cannot act on.
 */
export function registrationFailureMessages(error: AuthError): string[] {
  if (error.code === 'server-error') {
    return [];
  }
  const messages: string[] = [];
  if (error.message) {
    messages.push(error.message);
  }
  for (const [field, fieldMessages] of Object.entries(error.fieldErrors ?? {})) {
    for (const fieldMessage of fieldMessages) {
      messages.push(field === 'base' ? fieldMessage : `${humanizeField(field)} ${fieldMessage}`);
    }
  }
  return messages;
}

function humanizeField(field: string): string {
  const spaced = field.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}
